import React from 'react';
import { useTranslation } from 'react-i18next';
import { Footprints, Train, Bike, type LucideIcon } from 'lucide-react';

interface WalkScoreCardProps {
  walkScore: number;
  transitScore: number;
  bikeScore: number;
}

const WalkScoreCard: React.FC<WalkScoreCardProps> = ({ walkScore, transitScore, bikeScore }) => {
  const { t } = useTranslation();

  const getWalkScoreLabel = (score: number): string => {
    if (score >= 90) return t('walkScore.walkersParadise');
    if (score >= 70) return t('walkScore.veryWalkable');
    if (score >= 50) return t('walkScore.somewhatWalkable');
    if (score >= 25) return t('walkScore.carDependent');
    return t('walkScore.veryCarDependent');
  };

  const getTransitScoreLabel = (score: number): string => {
    if (score >= 70) return t('walkScore.excellentTransit');
    if (score >= 50) return t('walkScore.goodTransit');
    return t('walkScore.someTransit');
  };

  const getBikeScoreLabel = (score: number): string => {
    if (score >= 70) return t('walkScore.veryBikeable');
    if (score >= 50) return t('walkScore.bikeable');
    return t('walkScore.somewhatBikeable');
  };

  const getScoreColor = (score: number): string => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-orange-600';
  };

  const getScoreBgColor = (score: number): string => {
    if (score >= 90) return 'bg-green-100';
    if (score >= 70) return 'bg-blue-100';
    if (score >= 50) return 'bg-yellow-100';
    return 'bg-orange-100';
  };

  const ScoreItem = ({ icon: Icon, score, label, type }: { icon: LucideIcon; score: number; label: string; type: string }) => (
    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
      <div className={`p-3 rounded-full ${getScoreBgColor(score)}`}>
        <Icon className={`h-6 w-6 ${getScoreColor(score)}`} />
      </div>
      <div className="flex-1">
        <div className="flex items-baseline gap-2">
          <span className={`text-3xl font-bold ${getScoreColor(score)}`}>{score}</span>
          <span className="text-sm text-gray-500">{t('walkScore.outOf100')}</span>
        </div>
        <div className="text-sm font-medium text-gray-700">{type}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('walkScore.transportationWalkability')}</h2>
      <div className="space-y-3">
        <ScoreItem
          icon={Footprints}
          score={walkScore}
          label={getWalkScoreLabel(walkScore)}
          type={t('walkScore.walkScore')}
        />
        <ScoreItem
          icon={Train}
          score={transitScore}
          label={getTransitScoreLabel(transitScore)}
          type={t('walkScore.transitScore')}
        />
        <ScoreItem
          icon={Bike}
          score={bikeScore}
          label={getBikeScoreLabel(bikeScore)}
          type={t('walkScore.bikeScore')}
        />
      </div>
      <p className="text-xs text-gray-500 mt-4">
        {t('walkScore.disclaimer')}
      </p>
    </div>
  );
};

export default WalkScoreCard;
